import { CompanyNameField } from "../fields/companyNameField";
import { PostalCodeField } from "../fields/postalCodeField";
import { toggleValidStep } from '../../actions';
import {
    COUNTRY_BELGIUM,
    COUNTRY_NETHERLANDS,
    EVENT_NAME_FIELDS_VALIDATE,
    EVENT_NAME_STEP_VALIDATE,
    POSTAL_CODE_BE,
    POSTAL_CODE_NL,
    POSTAL_CODE_OTHER
} from "../../helpers/consts";
import { AddressField } from "../fields/addressField";
import { VatNumberField } from "../fields/vatNumberField";
import { SourceSelectField } from "../fields/sourceField";
import { StreetNameField } from "../fields/streetNameField";
import { StepTitle } from "../stepTitle";
import { loadAddresses, saveAddresses } from '../../localStorage/addresses';
import { CountrySelectField } from "../fields/countrySelect";
import { CityField } from "../fields/cityField";

export class CompanyInformationStep {
    /**
     * Component that handles the company inforamtion step.
     * @constructor
     * @param {Object} options
     * @param {Store} options.store
     * @param {string} options.id - Step id string
     */
    constructor(options) {
        this.store = options.store;
        this.state = this.store.getState();
        this.translations = options.translations;
        this.isValid = false;
        this.id = options.id;
        const initialAddresses = {
            "_5237JL3": { "purpose": "woonfunctie", "postcode": "5237JL", "surface": 90, "municipality": { "id": "0796", "label": "'s-Hertogenbosch" }, "city": { "id": "1595", "label": "'s-Hertogenbosch" }, "letter": null, "geo": { "center": { "rd": { "type": "Point", "coordinates": [148480.932, 415561.967], "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::28992" } } }, "wgs84": { "type": "Point", "coordinates": [5.2928419, 51.7287513], "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } } } } }, "nen5825": { "postcode": "5237 JL", "street": "MEESTER GEPKENSSTRAAT" }, "addition": null, "number": 3, "year": 1990, "province": { "id": "30", "label": "Noord-Brabant" }, "id": "0796200000406316", "type": "Verblijfsobject", "street": "Meester Gepkensstraat", "_links": { "self": { "href": "https://api.postcodeapi.nu/v2/addresses/0796200000406316/" } } },
            "_5237JL5": { "purpose": "woonfunctie", "postcode": "5237JL", "surface": 90, "municipality": { "id": "0796", "label": "'s-Hertogenbosch" }, "city": { "id": "1595", "label": "'s-Hertogenbosch" }, "letter": null, "geo": { "center": { "rd": { "type": "Point", "coordinates": [148480.932, 415561.967], "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::28992" } } }, "wgs84": { "type": "Point", "coordinates": [5.2928419, 51.7287513], "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } } } } }, "nen5825": { "postcode": "5237 JL", "street": "MEESTER GEPKENSSTRAAT" }, "addition": null, "number": 5, "year": 1990, "province": { "id": "30", "label": "Noord-Brabant" }, "id": "0796200000406318", "type": "Verblijfsobject", "street": "Meester Gepkensstraat", "_links": { "self": { "href": "https://api.postcodeapi.nu/v2/addresses/0796200000406318/" } } },
        };
        const stateCountryField = this.state.formFields.find(field => field.id === 'companyCountry');
        const stateCountryFieldValue = stateCountryField ? stateCountryField.value : false;
        this.addresses = loadAddresses(initialAddresses);
        const productLanguage = this.state.product.language ? this.state.product.language.split('-')[1] : COUNTRY_NETHERLANDS;
        this.country = stateCountryFieldValue || productLanguage;
        this.createStep();
        this.update();
        this.validateStep();
        if (this.country === POSTAL_CODE_NL) {
            this.fillAddress();
        }
        this.store.subscribe(this.update.bind(this));
    }

    createStep() {
        const $fieldSet = document.createElement('div');
        const fieldSetHTML = `
<div class="fieldset">
    <div class="row">
        <div class="col-12 field form-group col--field--company-name"></div>
    </div>
    <div class="row">
        <div class="col-12 field form-group col--field--company-country"></div>
    </div>
    <div class="row">
        <div class="col-12 col-sm-6 field form-group col--field--postal-code"></div>
        <div class="col-12 col-sm-6 field form-group col--field--address"></div>
    </div>
    <div class="row row--address-message visually-hidden">
        <div class="col-12 col--address-message">
            <p class='alert alert-danger'>${__('Postcode and house number combination unknown.')}</p>
        </div>
    </div>
    <div class="row">
        <div class="col-12 col-sm-6 field form-group col--field--street"></div>
        <div class="col-12 col-sm-6 field form-group col--field--city"></div>
    </div>
    <div class="row">
        <div class="col-12 col-sm-6 field form-group col--field--vat-number"></div>
        <div class="col-12 col-sm-6 field form-group col--field--select-source"></div>
    </div>
</div>
        `;
        $fieldSet.classList.add('fieldset');
        $fieldSet.innerHTML = fieldSetHTML;

        this.$stepTitle = new StepTitle({
            store: this.store,
            titleId: `${this.id}_title`,
            title: this.translations.step_5 ? this.translations.step_5 : __('Hi %s, enter your company details'),
        });

        this.$rowAddressMessage = $fieldSet.querySelector('.row--address-message');

        this.$companyNameField = new CompanyNameField({
            store: this.store,
            stepId: this.id,
            id: 'companyName',
            label: __('Company Name'),
            placeholder: 'Vul je bedrijfsnaam in',
            $context: $fieldSet,
        });

        this.$countrySelectField = new CountrySelectField({
            store: this.store,
            stepId: this.id,
            id: 'companyCountry',
            label: __('Land'),
            $context: $fieldSet,
        });

        this.$countrySelectElem = this.$countrySelectField.getSelectElement();

        this.$postalCodeField = new PostalCodeField({
            store: this.store,
            stepId: this.id,
            id: 'postalCode',
            label: __('Postcode'),
            mode: this.country,
            $context: $fieldSet,
        });

        this.$addressField = new AddressField({
            store: this.store,
            stepId: this.id,
            id: 'address',
            label: __('House number + addition'),
            $context: $fieldSet,
        });

        this.$streetField = new StreetNameField({
            store: this.store,
            stepId: this.id,
            id: 'street',
            label: __('Street name'),
            readOnly: false,
            $context: $fieldSet,
        });
        if (this.country === COUNTRY_NETHERLANDS) {
            this.$streetField.setReadOnly(true);
            this.$streetField.hide();
        }

        this.$cityField = new CityField({
            store: this.store,
            stepId: this.id,
            id: 'city',
            label: __('Place'),
            readOnly: false,
            $context: $fieldSet,
        });

        if (this.country === COUNTRY_NETHERLANDS) {
            this.$cityField.setReadOnly(true);
            this.$cityField.hide();
        }

        this.$vatNumberField = new VatNumberField({
            store: this.store,
            stepId: this.id,
            id: 'vatNumber',
            label: __('VAT number'),
            $context: $fieldSet,
        });

        this.$sourceSelectField = new SourceSelectField({
            store: this.store,
            stepId: this.id,
            id: 'source',
            label: this.translations.source ? this.translations.source : __('Where do you know us from?'),
            translations: this.translations,
            $context: $fieldSet,
        });

        this.$sourceSelectElem = this.$sourceSelectField.getSelectElement();

        $fieldSet.querySelector('.col--field--company-name').appendChild(this.$companyNameField.render());
        $fieldSet.querySelector('.col--field--company-country').appendChild(this.$countrySelectField.render());
        $fieldSet.querySelector('.col--field--postal-code').appendChild(this.$postalCodeField.render());
        $fieldSet.querySelector('.col--field--address').appendChild(this.$addressField.render());
        $fieldSet.querySelector('.col--field--street').appendChild(this.$streetField.render());
        $fieldSet.querySelector('.col--field--city').appendChild(this.$cityField.render());
        $fieldSet.querySelector('.col--field--vat-number').appendChild(this.$vatNumberField.render());
        $fieldSet.querySelector('.col--field--select-source').appendChild(this.$sourceSelectField.render());

        $fieldSet.addEventListener(EVENT_NAME_STEP_VALIDATE, this.validateStep.bind(this));
        $fieldSet.addEventListener(EVENT_NAME_FIELDS_VALIDATE, () => {
            this.$companyNameField.validate();
            this.$postalCodeField.validate();
            this.$addressField.validate();
            this.$streetField.validate();
            this.$cityField.validate();
            this.$vatNumberField.validate();
            this.$sourceSelectField.validate();
        });

        this.$countrySelectElem.addEventListener('input', () => this.changeAddresFields());

        this.$el = $fieldSet;

        this.validateStep();
    }

    changeAddresFields() {
        switch (this.$countrySelectElem.value) {
        case COUNTRY_NETHERLANDS:
            this.country = COUNTRY_NETHERLANDS;
            this.$streetField.setReadOnly(true);
            this.$cityField.setReadOnly(true);
            this.$postalCodeField.setMode(POSTAL_CODE_NL);
            this.$postalCodeField.validate();
            break;

        case COUNTRY_BELGIUM:
            this.country = COUNTRY_BELGIUM;
            this.$postalCodeField.setMode(POSTAL_CODE_BE);
            this.$rowAddressMessage.classList.add('visually-hidden');
            this.$streetField.setReadOnly(false);
            this.$streetField.show();
            this.$cityField.setReadOnly(false);
            this.$cityField.show();
            break;

        default:
            this.$postalCodeField.setMode(POSTAL_CODE_OTHER);
            this.$rowAddressMessage.classList.add('visually-hidden');
            this.country = this.$countrySelectElem.value;
            this.$streetField.setReadOnly(false);
            this.$streetField.show();
            this.$cityField.setReadOnly(false);
            this.$cityField.show();
            break;
        }

        this.$vatNumberField.updateField(this.country);
    }

    validateStep() {
        if (this.country === COUNTRY_NETHERLANDS) {
            this.fillAddress().then(() => {
                this.isValid = this.$companyNameField.valid() &&
                    this.$postalCodeField.valid() &&
                    this.$addressField.valid() &&
                    this.$streetField.valid() &&
                    this.$cityField.valid() &&
                    this.$vatNumberField.valid() &&
                    this.$sourceSelectField.valid();
                this.store.dispatch(toggleValidStep(this.id, this.isValid));
            }).catch(() => {
                this.isValid = this.$companyNameField.valid() &&
                    this.$postalCodeField.valid() &&
                    this.$addressField.valid() &&
                    this.$streetField.valid() &&
                    this.$cityField.valid() &&
                    this.$vatNumberField.valid() &&
                    this.$sourceSelectField.valid();
                this.store.dispatch(toggleValidStep(this.id, this.isValid));
            });
        } else {
            this.isValid = this.$companyNameField.valid() &&
                this.$postalCodeField.valid() &&
                this.$addressField.valid() &&
                this.$streetField.valid() &&
                this.$cityField.valid() &&
                this.$vatNumberField.valid() &&
                this.$sourceSelectField.valid();
            this.store.dispatch(toggleValidStep(this.id, this.isValid));
        }
    }

    async fillAddress() {
        const postalCodeAndNumber = `_${this.$postalCodeField.getValue() + this.$addressField.getValue()}`;
        this.$streetField.show();
        this.$cityField.show();
        if (this.country === POSTAL_CODE_NL && this.$postalCodeField.valid() && this.$addressField.valid() && this.addresses[postalCodeAndNumber] === undefined) {
            this.getAddress()
                .then(this.handleErrors)
                .then(data => {
                    // eslint-disable-next-line no-underscore-dangle
                    if (data._embedded.addresses[0]) {
                        Object.defineProperty(this.addresses, postalCodeAndNumber, {
                            // eslint-disable-next-line prefer-destructuring, no-underscore-dangle
                            value: data._embedded.addresses[0],
                            writable: true,
                            enumerable: true,
                        });
                        saveAddresses(this.addresses);

                        this.$rowAddressMessage.classList.add('visually-hidden');
                        this.$streetField.setReadOnly(true);
                        this.$cityField.setReadOnly(true);
                        this.$streetField.setValue(this.addresses[postalCodeAndNumber].street);
                        this.$cityField.setValue(this.addresses[postalCodeAndNumber].city.label);
                    } else {
                        Object.defineProperty(this.addresses, postalCodeAndNumber, {
                            // eslint-disable-next-line prefer-destructuring, no-underscore-dangle
                            value: false,
                            writable: true,
                            enumerable: true,
                        });

                        saveAddresses(this.addresses);

                        this.$rowAddressMessage.classList.remove('visually-hidden');
                        this.$streetField.setReadOnly(false);
                        this.$cityField.setReadOnly(false);
                    }
                }) // JSON-string from `response.json()` call
                .catch(error => {
                    this.$streetField.setReadOnly(false);
                    this.$rowAddressMessage.classList.remove('visually-hidden');
                    this.$cityField.setReadOnly(false);
                    return error;
                });
        } else if (this.addresses[postalCodeAndNumber]) {
            this.$rowAddressMessage.classList.add('visually-hidden');
            this.$streetField.setReadOnly(true);
            this.$cityField.setReadOnly(true);
            this.$streetField.setValue(this.addresses[postalCodeAndNumber].street);
            this.$cityField.setValue(this.addresses[postalCodeAndNumber].city.label);
        } else if (this.addresses[postalCodeAndNumber] === false) {
            this.$rowAddressMessage.classList.remove('visually-hidden');
            this.$streetField.setReadOnly(false);
            this.$streetField.setNotice();
            this.$cityField.setReadOnly(false);
        } else if (this.country === POSTAL_CODE_NL) {
            this.$streetField.hide();
            this.$cityField.hide();
            this.$rowAddressMessage.classList.add('visually-hidden');
            this.$streetField.setValue('');
            this.$streetField.setPlacholder('');
            this.$cityField.setValue('');
        }
    }

    async getAddress() { // eslint-disable-line class-methods-use-this
        const url = `https://api.postcodeapi.nu/v2/addresses/?postcode=${this.$postalCodeField.getValue()}&number=${this.$addressField.getNumber()}&addition=${this.$addressField.getSuffix()}`;
        const postalCodeApiHeader = new Headers({
            'x-api-key': this.state.settings.postal_code_key,
            'accept': 'application/hal+json'
        });

        try {
            const res = await fetch(url, {
                method: 'GET',
                credentials: 'omit',
                mode: 'cors',
                returnRateLimit: true,
                headers: postalCodeApiHeader,
            });
            const response = await res.json();

            return response;
        }
        catch (error) {
            return console.error('Error:', error);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    handleErrors(response) {
        if (!response || !response.status === 200) {
            throw Error(response ? response.status : 'No response');
        }
        return response;
    }

    render() {
        return this.$el;
    }

    update() {
        this.state = this.store.getState();
    }
}

export default CompanyInformationStep;
